import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import { orders } from '../../services/apiClient';

const PAYMENT_METHOD_LABELS = {
  credit_card: 'Cartao de credito',
  debit_card: 'Cartao de debito',
  pix: 'PIX',
  cash: 'Dinheiro',
  voucher: 'Voucher',
  card: 'Cartao',
};

function getPaymentMethodLabel(value) {
  const key = String(value || '').trim().toLowerCase();
  return PAYMENT_METHOD_LABELS[key] || value || '-';
}

function getVoucherIssuerLabel(value) {
  const key = String(value || '').trim().toLowerCase();
  if (key === 'ticket') return 'Ticket';
  if (key === 'pluxee') return 'Pluxee';
  if (key === 'biq') return 'Biq';
  if (key === 'unknown') return 'Desconhecido';
  return value || '-';
}

function getVoucherTypeLabel(value) {
  const key = String(value || '').trim().toLowerCase();
  if (key === 'alimentacao') return 'Alimentacao';
  if (key === 'refeicao') return 'Refeicao';
  if (key === 'unknown') return 'Desconhecido';
  return value || '-';
}

function getVoucherBrandLabel(value) {
  const key = String(value || '').trim().toLowerCase();
  if (!key) return '-';
  if (key === 'elo') return 'Elo';
  if (key === 'unknown') return 'Desconhecido';
  return value || '-';
}

function getVoucherMetaLabel(row) {
  const issuer = row?.voucher_issuer || row?.voucher?.voucher_issuer || row?.voucher?.issuer;
  const type = row?.voucher_type || row?.voucher?.voucher_type || row?.voucher?.type;
  const brand = row?.voucher_brand || row?.voucher?.voucher_brand || row?.voucher?.brand;
  if (!issuer && !type && !brand) return '-';
  return `${getVoucherIssuerLabel(issuer)} / ${getVoucherTypeLabel(type)} / ${getVoucherBrandLabel(brand)}`;
}

function getPaymentStatusLabel(value) {
  const key = String(value || '').trim().toLowerCase();
  if (!key) return '-';
  if (key === 'approved') return 'Aprovado';
  if (key === 'rejected') return 'Recusado';
  if (key === 'pending' || key === 'pending_cash') return 'Pendente';
  if (key === 'cancelled') return 'Cancelado';
  if (key === 'error' || key === 'technical_failure' || key === 'pending_error') return 'Falha tecnica';
  return value || '-';
}

function getCashReturnStatusLabel(value) {
  const key = String(value || '').trim().toLowerCase();
  if (!key) return '-';
  if (key === 'pending_return') return 'Pendente devolucao';
  if (key === 'code_generated') return 'Codigo gerado';
  if (key === 'overdue') return 'Vencida';
  if (key === 'settled') return 'Devolvida';
  if (key === 'cancelled') return 'Cancelada';
  return value || '-';
}

function getCashReturnLabel(row) {
  const payload = row?.cash_return && typeof row.cash_return === 'object' ? row.cash_return : null;
  if (!payload) return '-';
  const amountCents = Number(payload.amount_total_cents || 0);
  const amount = Number.isFinite(amountCents) ? (amountCents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : 'R$ 0,00';
  return `${getCashReturnStatusLabel(payload.status)} | ${amount}`;
}

function exportCsv(rows) {
  const headers = ['Pedido', 'Loja', 'Status', 'Total', 'Pagamento', 'Status Pagamento', 'Devolucao cash', 'Voucher', 'Data'];
  const csvRows = [headers.join(',')];

  rows.forEach((row) => {
    csvRows.push([
      row.order_number,
      row.restaurant_name,
      row.status,
      row.total,
      getPaymentMethodLabel(row.payment_method),
      getPaymentStatusLabel(row.payment_status),
      getCashReturnLabel(row),
      getVoucherMetaLabel(row),
      row.created_at
    ].join(','));
  });

  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'pedidos.csv');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export default function AdminOrders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({ status: '', payment: '', paymentStatus: '', storeId: '' });
  const [storeFilterName, setStoreFilterName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const storeId = searchParams.get('store_id') || '';
    const storeName = searchParams.get('store_name') || '';
    setFilters((prev) => ({ ...prev, storeId }));
    setStoreFilterName(storeName);
  }, [searchParams]);

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const list = await orders.list({
          status: filters.status || undefined,
          payment: filters.payment || undefined,
          paymentStatus: filters.paymentStatus || undefined,
          storeId: filters.storeId || undefined
        });
        if (mounted) setData(list);
      } catch {
        if (mounted) setError('Erro ao carregar pedidos');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => { mounted = false; };
  }, [filters.payment, filters.paymentStatus, filters.status, filters.storeId]);

  const clearStoreFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('store_id');
    next.delete('store_name');
    setSearchParams(next);
  };

  const columns = useMemo(() => [
    { key: 'order_number', header: 'Pedido' },
    { key: 'restaurant_name', header: 'Loja' },
    { key: 'status', header: 'Status' },
    { key: 'total', header: 'Total', render: (row) => `R$ ${Number(row.total).toFixed(2)}` },
    { key: 'payment_method', header: 'Pagamento', render: (row) => getPaymentMethodLabel(row.payment_method) },
    { key: 'payment_status', header: 'Status pagamento', render: (row) => getPaymentStatusLabel(row.payment_status) },
    { key: 'cash_return', header: 'Devolucao cash', render: (row) => getCashReturnLabel(row) },
    { key: 'voucher_meta', header: 'Voucher', render: (row) => getVoucherMetaLabel(row) },
    { key: 'created_at', header: 'Data' }
  ], []);

  return (
    <div className="admin-section">
      <PageHeader
        title="Pedidos"
        subtitle="Acompanhe e exporte pedidos"
        actions={<button className="admin-button" onClick={() => exportCsv(data)}>Exportar CSV</button>}
      />

      <div className="admin-filters">
        <select className="admin-select" value={filters.status} onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}>
          <option value="">Status</option>
          <option value="pending">Pendente</option>
          <option value="confirmed">Confirmado</option>
          <option value="preparing">Preparando</option>
          <option value="ready">Pronto</option>
          <option value="out_for_delivery">Em entrega</option>
          <option value="delivered">Entregue</option>
          <option value="cancelled">Cancelado</option>
        </select>
        <select className="admin-select" value={filters.payment} onChange={(e) => setFilters((prev) => ({ ...prev, payment: e.target.value }))}>
          <option value="">Pagamento</option>
          <option value="pix">Pix</option>
          <option value="cash">Dinheiro</option>
          <option value="credit_card">Cartao de credito</option>
          <option value="debit_card">Cartao de debito</option>
          <option value="card">Cartao (legado)</option>
          <option value="voucher">Voucher</option>
        </select>
        <select className="admin-select" value={filters.paymentStatus} onChange={(e) => setFilters((prev) => ({ ...prev, paymentStatus: e.target.value }))}>
          <option value="">Status pagamento</option>
          <option value="approved">Aprovado</option>
          <option value="pending">Pendente</option>
          <option value="rejected">Recusado</option>
          <option value="cancelled">Cancelado</option>
          <option value="error">Falha tecnica</option>
          <option value="technical_failure">Falha tecnica (legacy)</option>
        </select>
        {filters.storeId ? (
          <button className="admin-button secondary" onClick={clearStoreFilter}>
            Loja: {storeFilterName || filters.storeId} (limpar)
          </button>
        ) : null}
      </div>

      <DataTable columns={columns} data={data} loading={loading} error={error} emptyMessage="Nenhum pedido encontrado" />
    </div>
  );
}
